import { Prisma } from "@prisma/client";
import { BuyableItemEvent, BuyableItemHandler } from "../buyable-item.types";

type PartialCashRegisterCreditsBuyableItem = {
  cashRegisterCreditsEvent?: { id: string } | null;
};

type CashRegisterCreditsBuyableItem = {
  cashRegisterCreditsEvent: { id: string };
};

type CashRegisterCreditsWithEvent = {
  cashRegisterCreditsEvent: BuyableItemEvent;
};

const GROUP_KEY_PREFIX = "cashRegisterCredits";

export const CashRegisterCreditsBuyableItemHandler = {
  displayName: "Pack de 100 crédits caisse",
  isCumulative: true,
  accounting: {
    generalAccountCode: "706512",
    invoiceLabel: "Pack de 100 crédits du logiciel de caisse",
  },

  getEvent(item: CashRegisterCreditsWithEvent): BuyableItemEvent {
    return item.cashRegisterCreditsEvent;
  },

  isValid(item: PartialCashRegisterCreditsBuyableItem): boolean {
    return !!item.cashRegisterCreditsEvent?.id;
  },

  findEquivalent(item: PartialCashRegisterCreditsBuyableItem): Prisma.BuyableItemWhereInput {
    return {
      cashRegisterCreditsEvent: {
        id: item.cashRegisterCreditsEvent?.id,
      },
    };
  },

  create({
    cashRegisterCreditsEvent,
  }: CashRegisterCreditsBuyableItem): Omit<Prisma.BuyableItemCreateInput, "buyableType"> {
    return {
      cashRegisterCreditsEvent: {
        connect: {
          id: cashRegisterCreditsEvent.id,
        },
      },
    };
  },

  parseGroupKeyAsWhere(groupKey: string): Prisma.BuyableItemWhereInput {
    if (!CashRegisterCreditsBuyableItemHandler.isGroupKey(groupKey)) {
      throw new Error(`Invalid group key for cash register credits: ${groupKey}`);
    }

    const [, eventId] = groupKey.split("|");
    return {
      cashRegisterCreditsEvent: {
        id: eventId,
      },
    };
  },

  isGroupKey(groupKey: string): boolean {
    return groupKey.startsWith(`${GROUP_KEY_PREFIX}|`);
  },

  getGroupKey(item: PartialCashRegisterCreditsBuyableItem): string {
    const eventId = item.cashRegisterCreditsEvent?.id;
    if (!eventId) {
      throw new Error("Cannot get group key for invalid cashRegisterCredits item");
    }
    return `${GROUP_KEY_PREFIX}|${eventId}`;
  },

  getPricingQuery(): Prisma.PricingFindFirstArgs {
    return {
      where: {
        backofficePrestation: {
          type: "cashRegisterCredits",
        },
      },
    };
  },

  getEventSearchFilter(searchText: string): Prisma.BuyableItemWhereInput {
    return {
      cashRegisterCreditsEvent: {
        name: { contains: searchText, mode: "insensitive" },
      },
    };
  },
} as const satisfies BuyableItemHandler;
